import React, { useContext, useEffect, useState, useCallback } from 'react';
import { UserContext } from '../userContext/UserContext'; // Access the userId from the context
import { fetchAssignedData, deleteIssue } from '../../services/apiService'; // Import the fetchAssignedData function
import './assigned.css'; // Import custom styles for the table if needed
import Update from '../update/Update';
import ContextMenu from '../contextMenu/ContextMenu';
import DeleteConfirmationModal from '../delete/Delete'; 

const Assigned = () => {
    const { userName, userId } = useContext(UserContext); // Get the logged-in user's ID
    const [assignedTasks, setAssignedTasks] = useState([]);
    const [error, setError] = useState(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showResolved, setShowResolved] = useState(false);
    const [showClosed, setShowClosed] = useState(false);

    const today = new Date();

    const formatDate = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        return date.toISOString().slice(0,10);
    }

    const fetchData = useCallback(async () => {
        try {
            const data = await fetchAssignedData(userId);
            const formattedData = data.map((issue) => ({
                ...issue,
                DueDate: formatDate(issue.DueDate),
                DateReported: formatDate(issue.DateReported)
            }))
            setAssignedTasks(formattedData);
        } catch (error) {
            setError(error.message);
        }
    }, [userId]);

    useEffect(() => {
        fetchData()
    }, [fetchData]);

    const filteredTasks = assignedTasks.filter(issue => {
        const allowedStatuses = ['active'];
    
        if (showResolved) allowedStatuses.push('resolved');
        if (showClosed) allowedStatuses.push('closed');
    
        return allowedStatuses.includes(issue.Status.toLowerCase());
    });
    

    const handleRowClick = (issue) => {
        setSelectedIssue(issue);  // Set the clicked issue as the selected issue
        setShowUpdateModal(true); // Show the update modal
    };

    const handleRowRightClick = (issue, event) => {
        event.preventDefault();
        setSelectedIssue(issue);
        setSelectedId(issue.id);  // Set selectedId to issue.id
        setContextMenuPosition({ x: event.pageX, y: event.pageY });
        setShowContextMenu(true);
    };

    const handleClickOutside = () => {
        setShowContextMenu(false);
        setSelectedId(null);  // Reset selectedId when clicking outside
    };

    const handleUpdateClick = () => {
        setShowContextMenu(false);
        setShowUpdateModal(true);
    };

    const handleDeleteClick = () => {
        setShowContextMenu(false);
        setShowDeleteModal(true); // Show the delete confirmation modal
    };

    const confirmDelete = async () => {
        try {
            await deleteIssue(selectedIssue);

            setShowDeleteModal(false);

            setAssignedTasks(prevIssues => prevIssues.filter(issue => issue.id !== selectedIssue.id));

            setSelectedIssue(null);
        } catch (error) {
            console.error('Error deleting issue:', error);
        }
    };

    const getDueDateClass = (dueDate) => {
        if (!dueDate) return '';
        const date = new Date(dueDate);
        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if(diffDays === 0){
            return 'due-today';
        } else if (diffDays < 0) {
            return 'due-past';
        } else if (diffDays <= 7) {
            return 'due-soon';
        } else {
            return 'due-future';
        }
    };

    
    if (error) return <p>Error: {error}</p>;

    return (
        <main className="assigned-container" onClick={handleClickOutside}>
            <div>
                <h2>Assigned Tasks ({userName})</h2>
                <h3>({filteredTasks.length}) issues assigned to you</h3>
                <div>
                    <p>Show:
                        <input
                            type="checkbox"
                            id="showResolved"
                            checked={showResolved}
                            onChange={() => setShowResolved(!showResolved)}
                        />
                        <label htmlFor="showResolved">Resolved</label>

                        <input
                            type="checkbox"
                            id="showClosed"
                            checked={showClosed}
                            onChange={() => setShowClosed(!showClosed)}
                        />
                        <label htmlFor="showClosed">Closed</label>
                    </p>
                </div>
                <div className="assigned-table-container">
                    {filteredTasks.length > 0 ? (
                        <table className="issues-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Job #</th>
                                    <th>Issue Name</th>
                                    <th>Assigned To</th>
                                    <th>Due Date</th>
                                    <th>Severity</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                            {filteredTasks.map((issue) => (
                                <tr 
                                    key={issue.id} 
                                    onClick={() => handleRowClick(issue)}
                                    onContextMenu={(e) => handleRowRightClick(issue, e)}
                                    className={`issue-row ${selectedId === issue.id ? 'row-selected' : ''}`}  // Highlight based on selectedId
                                >
                                    <td>{issue.id}</td>
                                    <td><strong>{issue.Title}</strong></td>
                                    <td>{issue.IssueName || ''}</td>                               
                                    <td>
                                        {typeof issue.AssignedToName === 'string'
                                            ? issue.AssignedToName.split(',').map((name, index) => (
                                                <React.Fragment key={index}>
                                                    {name.trim()}
                                                    <br />
                                                </React.Fragment>
                                            ))
                                            : issue.AssignedToName || ''}
                                    </td>
                                    <td >
                                        <span className={getDueDateClass(issue.DueDate)}>
                                            {issue.DueDate || ''}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`severity-${issue.Severity.toLowerCase()}`}>
                                            {issue.Severity || ''}
                                        </span>
                                    </td> 
                                    <td>
                                        <span className={`status-${issue.Status.toLowerCase()}`}>
                                            {issue.Status || ''}    
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>No upcoming tasks due within the next 7 days</p>
                    )}
                    {showContextMenu && (
                        <ContextMenu
                            x={contextMenuPosition.x}
                            y={contextMenuPosition.y}
                            onUpdate={handleUpdateClick}
                            onDelete={handleDeleteClick}
                        />
                    )}
                    {showUpdateModal && selectedIssue && (
                    <div className="modal">
                        <div className="modal-content">
                            <span className="close" onClick={() => setShowUpdateModal(false)}>&times;</span>
                            <Update issue={selectedIssue} closeModal={() => {setShowUpdateModal(false); fetchData(); }}  />
                        </div>
                    </div>
                    )}
                    {showDeleteModal && (
                    <DeleteConfirmationModal
                        show={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={confirmDelete}
                    />
                    )}
                </div>
            </div>
        </main>
    );
};

export default Assigned;
